import React, { useState, useEffect } from "react";
import { PageHeaderLink } from "component/shared/page-header";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { useSelector, useDispatch } from "react-redux";
import { DashboardLayout } from "component/layout/dashboard";
import { PageProps } from "shared/page-props";
import * as env from "shared/env";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { FullPageCard } from "component/shared/full-page-card";
import { Switch } from "component/shared/switch";
import { styles } from "./style";
import { User } from "model/user";
import { saveUser } from "redux/slice/authentication";
import { updateAgents } from "redux/slice/agents";
import { updateOffices } from "redux/slice/offices";
import { RootState } from "redux/store";
import { setEnhancedBioEnabled, setBioPageLayout } from "redux/slice/broker-settings";
import { PhotoUploadDialogButton } from "component/shared/photo-upload-dialog-button";
import { AgentSortOrderType } from "type/agent-sort-order";
import { PageType } from "type/page-type";
import { OfficeSortOrderType } from "type/office-sort-order";
import { BioPageLayoutType } from "type/bio-page-layout";
import { Agent } from "model/agent";
import { Office } from "model/office";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Card } from "./card";
import { OfficeCard } from "./office-card";
import { deepCompare } from "shared/deep-compare";
import { getAgentsByCustomSort, getOfficesByCustomSort, getPageByTypeId } from "redux/selector";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import * as paths from "routes/paths";
import bioLayoutHorizontal from "image/bio-layout-horizontal.png";
import bioLayoutVertical from "image/bio-layout-vertical.png";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";

interface Props extends PageProps, WithStyles<typeof styles>, Router.RouteComponentProps {
	user: User;
}

const Component: React.FC<Props> = ({ user, classes = {}, history }) => {
	const dispatch = useDispatch<any>();
	const authLoading = useSelector((state: RootState) => state.authentication.loading);
	const authError = useSelector((state: RootState) => state.authentication.error);
	const agents = useSelector(getAgentsByCustomSort);
	const offices = useSelector(getOfficesByCustomSort);
	// Links to the client's live pages, same derivation the Offices list header uses.
	const officesPage = useSelector((state: RootState) => getPageByTypeId(state, PageType.OFFICES.id));
	const teamPage = useSelector((state: RootState) => getPageByTypeId(state, PageType.AGENTS.id));
	const officesPageUrl = officesPage ? user.baseUrl + officesPage.path : undefined;
	const teamPageUrl = teamPage ? user.baseUrl + teamPage.path : undefined;
	const isKestrel = user.kestrelVersion.all;

	const [sendAgentsLeadTextNotifications, setSendAgentsLeadTextNotifications] = useState(
		user.sendAgentsLeadTextNotifications || false
	);

	const [isEnhancedBioEnabled, setIsEnhancedBioEnabled] = useState(user.isEnhancedBioEnabled ?? false);
	const [bioPageLayout, setBioPageLayoutState] = useState(BioPageLayoutType.getByIdOrDefault(user.bioPageLayout === 2 ? "layout2" : "layout1"));
	const [logoImageSource, setLogoImageSource] = useState<"upload" | "url">("url");
	const [logoUrl, setLogoUrl] = useState(user.controlPanelUrl || "");
	const [agentSortOrder, setAgentSortOrder] = useState(user.agentSortOrder);
	const [officeSortOrder, setOfficeSortOrder] = useState(user.officeSortOrder);
	const [officeOrder, setOfficeOrder] = useState<Office[]>([...offices]);
	const [agentOrder, setAgentOrder] = useState<Agent[]>([...agents]);
	const [positionInputs, setPositionInputs] = useState<Record<number, string>>(() =>
		agents.reduce((acc: Record<number, string>, agent, i) => ({ ...acc, [agent.id]: String(i + 1) }), {})
	);
	const [positionErrors, setPositionErrors] = useState<Record<number, string>>({});

	const syncPositionInputs = (order: Agent[]) =>
		order.reduce((acc: Record<number, string>, agent, i) => ({ ...acc, [agent.id]: String(i + 1) }), {});

	useEffect(() => {
		if (!deepCompare(agents, agentOrder)) {
			setAgentOrder([...agents]);
			setPositionInputs(syncPositionInputs(agents));
			setPositionErrors({});
		}
	}, [agents]);

	useEffect(() => {
		if (!deepCompare(offices, officeOrder)) {
			setOfficeOrder([...offices]);
		}
	}, [offices]);

	const isDirty = () =>
		sendAgentsLeadTextNotifications !== (user.sendAgentsLeadTextNotifications || false) ||
		logoUrl !== (user.controlPanelUrl || "") ||
		logoImageSource !== "url" ||
		agentSortOrder.id !== user.agentSortOrder.id ||
		officeSortOrder.id !== user.officeSortOrder.id ||
		!deepCompare(officeOrder, offices) ||
		isEnhancedBioEnabled !== (user.isEnhancedBioEnabled ?? false) ||
		bioPageLayout.id !== BioPageLayoutType.getByIdOrDefault(user.bioPageLayout === 2 ? "layout2" : "layout1").id ||
		!deepCompare(agentOrder, agents);

	const cancel = () => {
		setSendAgentsLeadTextNotifications(user.sendAgentsLeadTextNotifications || false);
		setIsEnhancedBioEnabled(user.isEnhancedBioEnabled ?? false);
		setBioPageLayoutState(BioPageLayoutType.getByIdOrDefault(user.bioPageLayout === 2 ? "layout2" : "layout1"));
		setLogoImageSource("url");
		setLogoUrl(user.controlPanelUrl || "");
		setAgentSortOrder(user.agentSortOrder);
		setOfficeSortOrder(user.officeSortOrder);
		setOfficeOrder([...offices]);
		setAgentOrder([...agents]);
		setPositionInputs(syncPositionInputs(agents));
		setPositionErrors({});
		dispatch(setEnhancedBioEnabled(user.isEnhancedBioEnabled ?? false));
		dispatch(setBioPageLayout(user.bioPageLayout === 2 ? "layout2" : "layout1"));
	};

	const handlePositionChange = (agentId: number, value: string) => {
		setPositionInputs(prev => ({ ...prev, [agentId]: value }));

		const num = parseInt(value, 10);

		if (!value || isNaN(num)) {
			setPositionErrors(prev => ({ ...prev, [agentId]: "" }));
			return;
		}

		if (num < 1 || num > agentOrder.length) {
			setPositionErrors(prev => ({
				...prev,
				[agentId]: `Enter a number between 1 and ${agentOrder.length}`,
			}));
			return;
		}

		// Check if another agent's input already shows this position (conflict)
		const conflict = agentOrder.some(
			a => a.id !== agentId && positionInputs[a.id] === value
		);
		if (conflict) {
			setPositionErrors(prev => ({ ...prev, [agentId]: "Number already assigned" }));
			return;
		}

		// Valid — reorder immediately
		const currentIndex = agentOrder.findIndex(a => a.id === agentId);
		const targetIndex = num - 1;

		if (currentIndex === targetIndex) {
			setPositionErrors(prev => ({ ...prev, [agentId]: "" }));
			return;
		}

		const newOrder = [...agentOrder];
		const [agent] = newOrder.splice(currentIndex, 1);
		newOrder.splice(targetIndex, 0, agent);

		setAgentOrder(newOrder);
		setPositionInputs(syncPositionInputs(newOrder));
		setPositionErrors({});
	};

	const onDragEnd = (result: any) => {
		const { destination, source } = result;
		if (!destination) return;
		if (destination.droppableId === source.droppableId && destination.index === source.index) {
			return;
		}

		const items = Array.from(agentOrder);
		const [reordered] = items.splice(source.index, 1);
		items.splice(destination.index, 0, reordered);
		setAgentOrder(items);
		setPositionInputs(syncPositionInputs(items));
		setPositionErrors({});
	};

	const onOfficeDragEnd = (result: any) => {
		const { destination, source } = result;
		if (!destination) return;
		if (destination.droppableId === source.droppableId && destination.index === source.index) {
			return;
		}
		const items = Array.from(officeOrder);
		const [reordered] = items.splice(source.index, 1);
		items.splice(destination.index, 0, reordered);
		setOfficeOrder(items);
	};

	const save = () => {
		const updateUser = {
			...user,
			sendAgentsLeadTextNotifications,
			controlPanelUrl: logoUrl,
			agentSortOrder,
			officeSortOrder,
			isEnhancedBioEnabled,
			bioPageLayout: bioPageLayout.id === "layout2" ? 2 : 1,
		};
		dispatch(saveUser({ user: updateUser }));

		const agentsToUpdate = agentOrder.map((agent) => {
			const { ...agentData } = agent;
			agentData.sortOrder = undefined;
			return agentData;
		});
		dispatch(updateAgents({ agents: agentsToUpdate }));

		// Only ids matter — the server renumbers priority from the array order.
		if (!deepCompare(officeOrder, offices)) {
			dispatch(updateOffices({ offices: officeOrder }));
		}
	};

	const title = env.refreshDesignsDev ? "General" : "Agent/Office Basic Settings";

	return (
		<DashboardLayout
			permitted={user && user.permissions.broker}
			title={title}
			fullScreen
			header={
				<>
					<Mui.Grid container direction="row" alignItems="center" justifyContent="space-between">
						<Mui.Typography variant="h1">
							<FeatherIcon>
								<Icons.Settings />
							</FeatherIcon>
							{title}
						</Mui.Typography>
						{env.refreshDesignsDev && (
							<PageHeaderLink to={paths.settingsAgents}>View Teams Page</PageHeaderLink>
						)}
					</Mui.Grid>
					</>
			}
		>
			<UnsavedChangesPrompt isDirty={isDirty} history={history} />
			<Mui.Typography variant="body2" className={classes.pageSubtitle}>Manage general settings for your team, including agent display preferences, notifications, and branding.</Mui.Typography>
			<Mui.Grid
				container
				direction="column"
				justifyContent="center"
				alignItems="stretch"
				style={{ width: "100%", margin: 0, gap: 16 }}
			>
				{/* Agent Bio Pages Section - Only for kestrel (v10) accounts */}
				{process.env.REACT_APP_ENHANCED_BIO_ENABLED === 'true' && user.kestrelVersion.all && user.permissions.enhancedAgentBioPages && (
					<Mui.Grid item>
						<FullPageCard>
							<Mui.Grid container direction="column" spacing={2}>
								<Mui.Grid item>
									<Mui.Typography className={classes.sectionHeading}>Agent Bio Pages</Mui.Typography>
								</Mui.Grid>
								<Mui.Grid item>
									<Switch
										checked={isEnhancedBioEnabled}
										onChange={(value) => { setIsEnhancedBioEnabled(value); dispatch(setEnhancedBioEnabled(value)); }}
										label="Use enhanced bio pages"
										description="When enabled, agent bio pages will use a modern, mobile-responsive layout designed to build credibility and capture leads, replacing the original layout."
									/>
									{isEnhancedBioEnabled && (
										// MAX-3690: indent the disclosed layout block to align with the toggle's
										// label. The switch control occupies ~59px (FormControlLabel -11 margin +
										// switch 8/46/8 margins/width + label 8 margin), so the label starts there.
										(<Mui.Box mt={2} ml="59px">
											<Mui.Typography className={classes.bioLayoutPrompt}>Select your preferred layout for your agent bio pages:</Mui.Typography>
											<Mui.RadioGroup
												value={bioPageLayout.id}
												onChange={(e) => { setBioPageLayoutState(BioPageLayoutType.getByIdOrDefault(e.target.value)); dispatch(setBioPageLayout(e.target.value)); }}
											>
												<Mui.Box className={classes.layoutOptionsContainer}>
													{/* Layout 1 - Horizontal */}
													<Mui.Box
														onClick={() => { setBioPageLayoutState(BioPageLayoutType.HORIZONTAL); dispatch(setBioPageLayout(BioPageLayoutType.HORIZONTAL.id)); }}
														className={classes.layoutOption}
													>
														<Mui.Radio
															checked={bioPageLayout === BioPageLayoutType.HORIZONTAL}
															value={BioPageLayoutType.HORIZONTAL.id}
															className={classes.layoutRadio}
														/>
														<Mui.Box style={{ marginLeft: 8 }}>
															<Mui.Typography variant="body2" style={{ marginBottom: 4, marginLeft: 16 }}>Horizontal</Mui.Typography>
															<img
																src={bioLayoutHorizontal}
																alt="Horizontal layout"
																className={classes.layoutImage}
															/>
														</Mui.Box>
													</Mui.Box>
													{/* Layout 2 - Vertical */}
													<Mui.Box
														onClick={() => { setBioPageLayoutState(BioPageLayoutType.VERTICAL); dispatch(setBioPageLayout(BioPageLayoutType.VERTICAL.id)); }}
														className={classes.layoutOption}
													>
														<Mui.Radio
															checked={bioPageLayout === BioPageLayoutType.VERTICAL}
															value={BioPageLayoutType.VERTICAL.id}
															className={classes.layoutRadio}
														/>
														<Mui.Box style={{ marginLeft: 8 }}>
															<Mui.Typography variant="body2" style={{ marginBottom: 4, marginLeft: 16 }}>Vertical</Mui.Typography>
															<img
																src={bioLayoutVertical}
																alt="Vertical layout"
																className={classes.layoutImage}
															/>
														</Mui.Box>
													</Mui.Box>
												</Mui.Box>
											</Mui.RadioGroup>
										</Mui.Box>)
									)}
								</Mui.Grid>
							</Mui.Grid>
						</FullPageCard>
					</Mui.Grid>
				)}

				{/* Lead Notifications */}
				{user.permissions.showTeamFeatures && (
					<Mui.Grid item>
						<FullPageCard>
							<Mui.Grid container direction="column" spacing={2}>
								<Mui.Grid item>
									<Mui.Typography className={classes.sectionHeading}>Lead Notifications</Mui.Typography>
								</Mui.Grid>
								<Mui.Grid item>
									<Switch
										checked={sendAgentsLeadTextNotifications}
										onChange={(value) => setSendAgentsLeadTextNotifications(value)}
										label="Send agent lead notifications via text message"
										description="When enabled, agents will receive text notifications to the cell phone number on their profile for information requests, contact requests, showing requests, and valuation requests."
									/>
								</Mui.Grid>
							</Mui.Grid>
						</FullPageCard>
					</Mui.Grid>
				)}

				{/* Control Panel Logo */}
				<Mui.Grid item>
					<FullPageCard>
						<Mui.Grid container direction="column" spacing={2}>
							<Mui.Grid item>
								<Mui.Typography className={classes.sectionHeading}>Control Panel Logo</Mui.Typography>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Typography variant="body2" className={classes.contentDescription}>Customize the logo that displays in your Control Panel header.</Mui.Typography>
								<Mui.FormControl component="fieldset" fullWidth>
									<Mui.RadioGroup
										value={logoImageSource}
										onChange={(e) => setLogoImageSource(e.target.value as "upload" | "url")}
									>
										<Mui.FormControlLabel
											value="url"
											control={<Mui.Radio color="secondary" />}
											style={{ alignItems: 'flex-start' }}
											label={
												<Mui.Box>
													<Mui.Typography variant="body1" style={{ marginTop: 9 }}>Image URL</Mui.Typography>
													{logoImageSource === "url" && (
														<Mui.TextField
															fullWidth
															inputProps={{ maxLength: 100 }}
															label="URL"
															onChange={(event) => setLogoUrl(event.target.value)}
															value={logoUrl}
															variant="outlined"
															style={{ marginTop: 8, width: 400 }}
														/>
													)}
												</Mui.Box>
											}
										/>
										<Mui.FormControlLabel value="upload" control={<Mui.Radio color="secondary" />} label="Upload Image" />
									</Mui.RadioGroup>
									{logoImageSource === "upload" && (
										<Mui.Box display="inline-block">
											<PhotoUploadDialogButton
												multiple={false}
												callback={(uploads) => {
													const upload = uploads.find((upload) => upload.url);
													if (upload && upload.url) {
														setLogoImageSource("url");
														setLogoUrl(upload ? upload.url : "");
													}
												}}
												label="Upload Image"
												color="primary"
												icon={<Icons.Plus size={16} />}
												style={{ marginBottom: 4, marginTop: 8, }}
												resourceType="image"
											/>
										</Mui.Box>
									)}
								</Mui.FormControl>
							</Mui.Grid>
						</Mui.Grid>
					</FullPageCard>
				</Mui.Grid>

				{/* Display Order — Offices, then Agents (MAX-4065). V9 sorts offices by priority only and
				    ignores both sort-order settings, so it gets just the office list (MAX-4393). */}
				{user.permissions.showTeamFeatures && (isKestrel || officeOrder.length > 0) && (
					<Mui.Grid item>
						<FullPageCard>
							<Mui.Grid container direction="column" spacing={2}>
								<Mui.Grid item>
									<Mui.Typography className={classes.sectionHeading}>Display Order</Mui.Typography>
								</Mui.Grid>

								<Mui.Grid item>
									<Mui.Typography className={classes.subsectionHeading}>Offices</Mui.Typography>
									<Mui.Typography variant="body2" className={classes.contentDescription}>
										Choose the order in which offices are displayed on your website's{" "}
										<Mui.Link
											href={officesPageUrl}
											target="_blank"
											rel="noopener noreferrer"
											className={classes.inlineLink}
										>
											Offices page
											<Icons.ExternalLink size={12} />
										</Mui.Link>
									</Mui.Typography>
									<Mui.FormControl component="fieldset">
										{isKestrel && (
											<Mui.RadioGroup
												value={officeSortOrder.id}
												onChange={(e) => setOfficeSortOrder(OfficeSortOrderType.getByIdOrDefault(e.target.value))}
											>
												{OfficeSortOrderType.values().map((type) => (
													<Mui.FormControlLabel
														key={type.id}
														value={type.id}
														control={<Mui.Radio color="secondary" />}
														label={type.label}
													/>
												))}
											</Mui.RadioGroup>
										)}
										{(!isKestrel || officeSortOrder.id === OfficeSortOrderType.CUSTOM.id) && officeOrder.length > 0 && (
											<Mui.Box style={{ marginTop: 16 }}>
												<Mui.Box style={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 8, overflow: "hidden", marginTop: 8 }}>
													<DragDropContext onDragEnd={onOfficeDragEnd}>
														<Droppable droppableId="officeOrder">
															{(provided) => (
																<div {...provided.droppableProps} ref={provided.innerRef}>
																	{officeOrder.map((office, index) => (
																		<Draggable
																			key={office.id}
																			draggableId={`office-${office.id}`}
																			index={index}
																		>
																			{(provided) => (
																				<div
																					ref={provided.innerRef}
																					{...provided.draggableProps}
																					{...provided.dragHandleProps}
																				>
																					<OfficeCard
																						office={office}
																						position={index + 1}
																						isLast={index === officeOrder.length - 1}
																					/>
																				</div>
																			)}
																		</Draggable>
																	))}
																	{provided.placeholder}
																</div>
															)}
														</Droppable>
													</DragDropContext>
												</Mui.Box>
											</Mui.Box>
										)}
									</Mui.FormControl>
								</Mui.Grid>

								{isKestrel && (
								<Mui.Grid item>
									<Mui.Typography className={classes.subsectionHeading}>Agents</Mui.Typography>
									<Mui.Typography variant="body2" className={classes.contentDescription}>
										Choose the order in which agents are displayed on your website's{" "}
										<Mui.Link
											href={teamPageUrl}
											target="_blank"
											rel="noopener noreferrer"
											className={classes.inlineLink}
										>
											Team page
											<Icons.ExternalLink size={12} />
										</Mui.Link>
									</Mui.Typography>
									<Mui.FormControl component="fieldset">
										<Mui.RadioGroup
											value={agentSortOrder.id}
											onChange={(e) => setAgentSortOrder(AgentSortOrderType.getByIdOrDefault(e.target.value))}
										>
											{AgentSortOrderType.values().map((type) => (
												<Mui.FormControlLabel
													key={type.id}
													value={type.id}
													control={<Mui.Radio color="secondary" />}
													label={type.label}
												/>
											))}
										</Mui.RadioGroup>
										{agentSortOrder.id === AgentSortOrderType.CUSTOM.id && (
											<Mui.Box style={{ marginTop: 16 }}>
												<Mui.Box style={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 8, overflow: "hidden", marginTop: 8 }}>
												<DragDropContext onDragEnd={onDragEnd}>
													<Droppable droppableId="droppable">
														{(provided) => (
															<div {...provided.droppableProps} ref={provided.innerRef}>
																{agentOrder.map((agent, index) => (
																	<Draggable
																		key={agent.id}
																		draggableId={String(agent.id)}
																		index={index}
																	>
																		{(provided) => (
																			<div
																				ref={provided.innerRef}
																				{...provided.draggableProps}
																				{...provided.dragHandleProps}
																			>
																				<Card
																					agent={agent}
																					position={index + 1}
																					inputValue={positionInputs[agent.id] ?? String(index + 1)}
																					onPositionChange={(value) => handlePositionChange(agent.id, value)}
																					error={positionErrors[agent.id]}
																					isLast={index === agentOrder.length - 1}
																					onDelete={() => {
																						const newOrder = [...agentOrder];
																						newOrder.splice(index, 1);
																						setAgentOrder(newOrder);
																						setPositionInputs(syncPositionInputs(newOrder));
																						setPositionErrors({});
																					}}
																				/>
																			</div>
																		)}
																	</Draggable>
																))}
																{provided.placeholder}
															</div>
														)}
													</Droppable>
												</DragDropContext>
												</Mui.Box>
											</Mui.Box>
										)}
							</Mui.FormControl>
							</Mui.Grid>
								)}
							</Mui.Grid>
						</FullPageCard>
					</Mui.Grid>
				)}

				<SettingsStickyFooter
					dirty={isDirty()}
					saving={!!authLoading}
					authError={authError}
					onCancel={cancel}
					onSave={save}
				/>
			</Mui.Grid>
		</DashboardLayout>
	);
};

const SettingsBrokerPageNew = withStyles(Router.withRouter(Component), styles);

import { SettingsBrokerPageLegacy } from "./legacy";

export const SettingsBrokerPage =
	env.refreshDesignsDev
		? SettingsBrokerPageNew
		: SettingsBrokerPageLegacy;
